import React, { useMemo } from 'react';
import { Animated, ScrollView, Text, View } from 'react-native';
import { CalendarCheck, Clock3, Coins, Medal, Target, Trophy, Users, Zap } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { SketchFrame } from '../../worldcup/WorldCupWelcomeScreen/SketchFrame';
import { SketchNote } from '../../worldcup/WorldCupWelcomeScreen/SketchNote';
import { WelcomeDisclaimerFooter } from '../../worldcup/WorldCupWelcomeScreen/WelcomeDisclaimerFooter';
// The World Cup rules phase's section styles — numbered step badge, emphasized title, framed
// preview. Reused rather than re-derived so the two intros cannot drift apart by hand, the same
// reason CompetitionScreenHeader reuses the WC header's style factory.
import { getWelcomeRulesPhaseStyles } from '../../worldcup/WorldCupWelcomeScreen/WelcomeRulesPhase.styles';
import {
  useWorldCupWelcomeStaggerEntrance,
  staggerStyle,
} from '../../worldcup/WorldCupWelcomeScreen/useWorldCupWelcomeStaggerEntrance';
import { SeasonMatchPickCard } from '../SeasonPicks/SeasonMatchPickCard';
import type {
  CompetitionIntroConfig,
  CompetitionIntroIconName,
  CompetitionIntroPreviewId,
  CompetitionIntroRuleItem,
} from '../../../src/competitions/types/competitionIntro.types';
import { COMPETITION_WELCOME_RULES_BLOCK_COUNT } from './CompetitionWelcomeScreen.constants';
import {
  COMPETITION_WELCOME_PREVIEW_FRAME_RADIUS,
  getCompetitionIntroPreviewMatches,
} from './CompetitionWelcomeRulesPhase.constants';
import { getCompetitionWelcomePhaseStyles } from './CompetitionWelcomeScreen.styles';
import { CompetitionWelcomeFooter } from './CompetitionWelcomeFooter';

const RULE_ICONS: Record<CompetitionIntroIconName, typeof Target> = {
  target: Target,
  scoring: Coins,
  boost: Zap,
  table: Trophy,
  squad: Users,
  clock: Clock3,
  futures: Medal,
};

/** The preview cards are illustrations: nothing they emit may reach a saver. */
const NO_OP_CHANGE_PICK = (): void => undefined;
const NO_OP_TOGGLE_BOOST = (): void => undefined;

export interface CompetitionWelcomeRulesPhaseProps {
  content: CompetitionIntroConfig;
  phaseIndex: number;
  phaseCount: number;
  isExiting: boolean;
  scrollContentPaddingTop: number;
  bottomInset: number;
  onNext: () => void;
  testID?: string;
  /** Stable across phases so a walkthrough can click "next" without knowing the beat. */
  ctaTestID?: string;
}

/**
 * Rules beat.
 *
 * A rule with a `preview` is drawn the World Cup way — numbered step, emphasized title, a real
 * pick card in a sketch frame, a handwritten note. A rule without one keeps the icon card the
 * screen has always used, so a competition can mix the two and older content stays valid.
 */
export const CompetitionWelcomeRulesPhase: React.FC<CompetitionWelcomeRulesPhaseProps> = ({
  content,
  phaseIndex,
  phaseCount,
  isExiting,
  scrollContentPaddingTop,
  bottomInset,
  onNext,
  testID,
  ctaTestID,
}) => {
  const theme = useTheme();
  const styles = useMemo(
    () => getCompetitionWelcomePhaseStyles(theme, { scrollContentPaddingTop, bottomInset }),
    [theme, scrollContentPaddingTop, bottomInset],
  );
  const sectionStyles = useMemo(
    () => getWelcomeRulesPhaseStyles(theme, { scrollContentPaddingTop }),
    [theme, scrollContentPaddingTop],
  );
  const stagger = useWorldCupWelcomeStaggerEntrance(
    phaseIndex,
    phaseIndex,
    COMPETITION_WELCOME_RULES_BLOCK_COUNT,
  );

  // One set per render, so an intro left open across a kickoff still shows a future match.
  const previews = useMemo(() => getCompetitionIntroPreviewMatches(), []);

  const renderPreview = (preview: CompetitionIntroPreviewId) => {
    const match = previews[preview];
    return (
      <SeasonMatchPickCard
        match={match}
        pick={match.prediction ? { home: match.prediction.home, away: match.prediction.away } : null}
        hasSaveError={false}
        onChangePick={NO_OP_CHANGE_PICK}
        boost={
          preview === 'boost'
            ? { eligible: true, active: true, placeable: true, onToggle: NO_OP_TOGGLE_BOOST }
            : null
        }
      />
    );
  };

  const renderPreviewSection = (item: CompetitionIntroRuleItem, index: number) => {
    const Icon = item.icon != null ? RULE_ICONS[item.icon] : CalendarCheck;
    const segments = item.titleSegments ?? [{ text: item.title }];
    return (
      <View style={sectionStyles.sectionBlock}>
        <View style={sectionStyles.sectionHeaderRow}>
          <View style={sectionStyles.sectionStepBadgeWrap}>
            <View style={sectionStyles.sectionStepBadge}>
              <Text style={sectionStyles.sectionStepBadgeText}>{index + 1}</Text>
            </View>
          </View>
          <View style={sectionStyles.sectionTitleRow}>
            <View style={sectionStyles.sectionIconWrap}>
              <Icon size={16} color={theme.primary} strokeWidth={2.2} />
            </View>
            <Text style={sectionStyles.sectionTitle}>
              {segments.map((segment, segmentIndex) => (
                <Text
                  key={segmentIndex}
                  style={segment.em ? sectionStyles.sectionTitleEm : undefined}
                >
                  {segment.text}
                </Text>
              ))}
            </Text>
          </View>
        </View>
        <View style={sectionStyles.previewContent}>
          <SketchFrame radius={COMPETITION_WELCOME_PREVIEW_FRAME_RADIUS} seed={index + 1}>
            {renderPreview(item.preview as CompetitionIntroPreviewId)}
          </SketchFrame>
        </View>
        {item.note ? <SketchNote text={item.note} side={item.noteSide ?? 'center'} /> : null}
      </View>
    );
  };

  const renderIconCard = (item: CompetitionIntroRuleItem, index: number) => {
    const Icon = item.icon != null ? RULE_ICONS[item.icon] : CalendarCheck;
    return (
      <SketchFrame radius={20} seed={index + 1}>
        <View style={styles.card}>
          <View style={styles.cardIconWrap}>
            <Icon size={20} color={theme.accent} strokeWidth={2.2} />
          </View>
          <View style={styles.cardBody}>
            <Text style={styles.ruleTitle}>{item.title}</Text>
            <Text style={styles.ruleDesc}>{item.desc}</Text>
          </View>
        </View>
      </SketchFrame>
    );
  };

  return (
    <View style={styles.container} testID={testID}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        testID={testID != null ? `${testID}-scroll` : undefined}
      >
        <Animated.View style={staggerStyle(stagger[0])}>
          <Text style={styles.eyebrow}>{content.rules.label}</Text>
          <Text style={styles.title}>{content.rules.title}</Text>
          <Text style={styles.subtitle}>{content.rules.subtitle}</Text>
        </Animated.View>

        {content.rules.items.map((item, i) => {
          /** Block 0 is the header; keep the last cards on the final block if content grows. */
          const block = stagger[Math.min(i + 1, COMPETITION_WELCOME_RULES_BLOCK_COUNT - 1)];
          return (
            <Animated.View key={item.title} style={[styles.cardSpacer, staggerStyle(block)]}>
              {item.preview != null ? renderPreviewSection(item, i) : renderIconCard(item, i)}
            </Animated.View>
          );
        })}

        <WelcomeDisclaimerFooter />
      </ScrollView>

      <View style={styles.footer} testID={testID != null ? `${testID}-footer` : undefined}>
        <CompetitionWelcomeFooter
          label={content.rules.cta}
          isExiting={isExiting}
          phaseIndex={phaseIndex}
          phaseCount={phaseCount}
          styles={styles}
          onPress={onNext}
          testID={ctaTestID}
        />
      </View>
    </View>
  );
};
